import PropTypes from 'prop-types';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Drawer, Typography, IconButton } from '@mui/material';
import useAuth from '../../../hooks/useAuth';
// components
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import Iconify from '../../../components/iconify';

const NAV_WIDTH = 280;
const NAV_COLLAPSED_WIDTH = 72;

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

Nav.propTypes = {
  account: PropTypes.object,
  collapsed: PropTypes.bool,
  onToggleNav: PropTypes.func,
};

const navConfiguration = [
  {
    title: 'Recordings',
    path: '/dashboard/app',
    icon: 'home',
  },
  {
    title: 'Workspaces',
    path: '/dashboard/workspaces',
    icon: 'workspace',
  },
  {
    title: 'Rules',
    path: '/dashboard/standards',
    icon: 'rules',
  },
  {
    title: 'Sensor Manager',
    path: '/dashboard/sensor-manager',
    icon: 'sensors',
  },
  {
    title: 'Account',
    path: '/dashboard/account',
    icon: 'account',
  },
  {
    title: 'Help & Guides',
    path: '/dashboard/help',
    icon: 'help',
  },
];

export default function Nav({ account, collapsed = false, onToggleNav }) {
  const drawerWidth = collapsed ? NAV_COLLAPSED_WIDTH : NAV_WIDTH;

  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('ROLE_ADMIN');

  const adminNavConfig = [
    {
      title: 'User Management',
      path: '/dashboard/admin/users',
      icon: 'people',
    },
    {
      title: 'Onboarding',
      path: '/dashboard/onboarding',
      icon: 'onboarding',
    },
  ];

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box sx={{ px: collapsed ? 1.5 : 2.5, py: 3, display: 'flex', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        {collapsed ? (
          <Box component="img" src="/favicon/favicon-32x32.png" alt="VPSI" sx={{ width: 32, height: 32 }} />
        ) : (
          <Logo />
        )}
      </Box>

      {!collapsed && (
        <Box sx={{ mb: 5, mx: 2.5 }}>
          <Link underline="none">
            <StyledAccount>
              <Box sx={{ ml: 1 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  Welcome back {account.username}!
                </Typography>
              </Box>
            </StyledAccount>
          </Link>
        </Box>
      )}

      <NavSection data={navConfiguration} collapsed={collapsed} />

      {isAdmin && (
        <>
          <Box sx={{ px: collapsed ? 1 : 2.5, pt: 2 }}>
            {!collapsed && (
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Admin
              </Typography>
            )}
          </Box>
          <NavSection data={adminNavConfig} collapsed={collapsed} />
        </>
      )}

      <Box sx={{ flexGrow: 1 }} />

      {onToggleNav && (
        <Box sx={{ px: collapsed ? 0 : 2.5, py: 2, display: 'flex', justifyContent: 'center' }}>
          <IconButton onClick={onToggleNav} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <Iconify icon={collapsed ? 'eva:arrow-ios-forward-fill' : 'eva:arrow-ios-back-fill'} />
          </IconButton>
        </Box>
      )}
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: 0,
        width: drawerWidth,
        transition: (theme) => theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
      }}
    >
      <Drawer
        open
        variant="persistent"
        PaperProps={{
          sx: {
            width: drawerWidth,
            bgcolor: 'background.default',
            borderRightStyle: 'dashed',
            overflowX: 'hidden',
            transition: (theme) => theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
          },
        }}
      >
        {renderContent}
      </Drawer>
    </Box>
  );
}
